import { useLocation } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

export const PROJECT_ORIGIN_PROJECTS = 'participantProjects';
export const PROJECT_ORIGIN_MY_PROJECTS = 'participantMyProjects';

export type ProjectOrigin =
  | typeof PROJECT_ORIGIN_PROJECTS
  | typeof PROJECT_ORIGIN_MY_PROJECTS;

export type ProjectBackRoute =
  | '/participants/projects'
  | '/participants/my-projects';

const storageKey = (projectId: string) =>
  `participant-project-origin:${projectId}`;

const isProjectOrigin = (
  value: string | null | undefined,
): value is ProjectOrigin =>
  value === PROJECT_ORIGIN_PROJECTS || value === PROJECT_ORIGIN_MY_PROJECTS;

/**
 * Records which list page the participant entered a project from, for
 * entry points that skip the project detail page (e.g. "Resume screener"
 * on a My projects card, which navigates straight to the apply route).
 */
export function rememberProjectOrigin(
  projectId: string,
  origin: ProjectOrigin,
) {
  try {
    globalThis.sessionStorage.setItem(storageKey(projectId), origin);
  } catch {
    // sessionStorage may be unavailable (blocked site data, private mode).
  }
}

/**
 * Drops the recorded origin. Called when the Back button is used so the
 * memory lives for one journey rather than the whole tab session.
 */
export function forgetProjectOrigin(projectId: string) {
  try {
    globalThis.sessionStorage.removeItem(storageKey(projectId));
  } catch {
    // sessionStorage may be unavailable (blocked site data, private mode).
  }
}

function readProjectOrigin(projectId: string): ProjectOrigin | undefined {
  try {
    const stored = globalThis.sessionStorage.getItem(storageKey(projectId));
    return isProjectOrigin(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resolves where the project page's Back button should lead: the Projects
 * home by default, or My projects when that's where the participant
 * originally entered the project from. The origin arrives via router
 * history state on the list-card links and is persisted per project in
 * sessionStorage so it survives the apply round trip (including full-page
 * OAuth redirects, where router state is lost).
 */
export function useProjectBackRoute(projectId: string): ProjectBackRoute {
  const previousRoute = useLocation({
    select: (location) => location.state.previousRoute,
  });

  // Resolved in an effect: sessionStorage isn't available during SSR, and
  // reading it during render would risk a hydration mismatch.
  const [backRoute, setBackRoute] = useState<ProjectBackRoute>(
    '/participants/projects',
  );

  useEffect(() => {
    if (isProjectOrigin(previousRoute)) {
      rememberProjectOrigin(projectId, previousRoute);
    }

    const origin = isProjectOrigin(previousRoute)
      ? previousRoute
      : readProjectOrigin(projectId);

    setBackRoute(
      origin === PROJECT_ORIGIN_MY_PROJECTS
        ? '/participants/my-projects'
        : '/participants/projects',
    );
  }, [previousRoute, projectId]);

  return backRoute;
}
